import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { FuelingListResponse, FuelingRecord, FuelingSummary } from './types';

interface ListParams {
  page?: number;
  limit?: number;
  vehicleId?: string;
  fuelType?: string;
  fromDate?: string;
  toDate?: string;
  isAnomaly?: string;
  sort?: string;
}

const KEY = {
  all: ['fueling'] as const,
  list: (p: ListParams) => ['fueling', 'list', p] as const,
  one: (id: string) => ['fueling', id] as const,
  summary: (p: Record<string, unknown>) => ['fueling', 'summary', p] as const,
};

export function useFuelingList(params: ListParams = {}) {
  return useQuery<FuelingListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<FuelingListResponse>('/fueling', { params });
      return data;
    },
  });
}

export function useFuelingSummary(params: { vehicleId?: string; fromDate?: string; toDate?: string } = {}) {
  return useQuery<{ data: FuelingSummary }>({
    queryKey: KEY.summary(params),
    queryFn: async () => {
      const { data } = await api.get<{ data: FuelingSummary }>('/fueling/summary', { params });
      return data;
    },
  });
}

export function useCreateFueling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      vehicleId: string;
      fuelType: string;
      quantityMl: number;
      priceCentsPerL: number;
      totalCents: number;
      odometerMeters?: number;
      isFull?: boolean;
      stationId?: string;
      driverId?: string;
      fueledAt: string;
      notes?: string;
      receiptUploadId?: string;
    }) => {
      const { data } = await api.post<{ data: FuelingRecord }>('/fueling', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useUpdateFueling(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data } = await api.patch<{ data: FuelingRecord }>(`/fueling/${id}`, input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useDeleteFueling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/fueling/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}

export function useImportFueling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (records: Array<Record<string, unknown>>) => {
      const { data } = await api.post<{ data: { imported: number; errors: Array<{ row: number; error: string }> } }>(
        '/fueling/import',
        { records },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.all }),
  });
}
